import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import CloseIcon from "@mui/icons-material/Close"
import CodeIcon from "@mui/icons-material/Code"
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  useMediaQuery,
  useTheme as useMuiTheme,
} from "@mui/material"
import {
  Button,
  InputText,
  NotificationUtils,
  Skeleton,
  Tab,
  Tabs,
  Tag,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import axios from "axios"
import sha256 from "crypto-js/sha256"
import { FormikProvider, useFormik } from "formik"
import i18n from "i18next"
import { v4 as uuidv4 } from "uuid"

import { VersionHistory } from "../../components/UI/VersionHistory"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { VersionChangeRequest } from "../../types/custom/rules"
import { DataFileGroup } from "../../types/generated/api/DataFileGroup"
import type { DataFileGroupList } from "../../types/generated/api/DataFileGroupList"
import type { PaginatedDataFileGroupListList } from "../../types/generated/api/PaginatedDataFileGroupListList"
import type { PaginatedScriptVersionList } from "../../types/generated/api/PaginatedScriptVersionList"
import type { ScriptGroupRetrieve } from "../../types/generated/api/ScriptGroupRetrieve"
import type { ScriptVersion } from "../../types/generated/api/ScriptVersion"
import { ScriptVersionRetrieve } from "../../types/generated/api/ScriptVersionRetrieve"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { createFileFromString, extractPageFromBackEndPaginationLink } from "../../utils/genericHelpers"
import { StaticAnalysisScript, StaticAnalysisScriptEs, isStaticAnalysisValid } from "../Workflows"
import type {
  CreateScriptRequest,
  CredentialsRequestType,
  CredentialsType,
  DeleteScriptCredsRequest,
  RequirementsDataFile,
  ScriptDialogFormik,
  ScriptDialogProps,
  SingleScriptProps,
  UpdateScriptRequest,
} from "./Interfaces"
import { ScriptTab, StatusColorMap, StatusTextMap } from "./Interfaces"
import {
  CustomRequirementsPackages,
  CustomScriptEditor,
  CustomScriptTesting,
} from "./components/CustomScriptComponents"
import { CustomScriptsSecrets } from "./components/CustomScriptSecrets"
import { ScriptLogs } from "./components/ScriptComponents"

import styles from "./Scripts.module.scss"

/**
 * Formats an array of raw credential metadata (key) into the
 * CredentialsType structure used by the UI component's state.
 * Initializes value based on type and whether the script is a duplicate.
 * Sets default 'isUpdated' and 'isNew' flags to false.
 *
 * @param {boolean} isDuplicate - Flag indicating if the script being edited is a duplicate of an existing one.
 * If true, non-UNDEFINED values are initialized as empty strings ("").
 * If false, non-UNDEFINED values are initialized as masked strings ("********").
 * @param {{ key: string; type: CredentialValueType }[] | null | undefined} creds - An array of objects containing the key
 * for each credential, or null/undefined.
 * @returns {CredentialsType[]} An array of credential objects formatted for the UI state, or an empty array if input creds is null/undefined.
 */
const formatCreds = (isDuplicate: boolean, creds?: { key: string }[] | null): CredentialsType[] => {
  if (!creds) return []

  return creds.map((cred) => ({
    key: cred.key,
    value: isDuplicate ? "" : "********",
    isUpdated: false,
    isNew: false,
  }))
}

/**
 * Builds the request payload for saving script credentials.
 * Determines which credentials were added, updated, or deleted based on
 * the original credentials and the current state.
 * Throws errors for invalid states (missing values for duplicates).
 *
 * @param {{ key: string }[] | null} oldCreds - An array containing the keys of the credentials that existed before editing, or null if it's a new script.
 * @param {CredentialsType[]} tableCreds - The current state of credentials as represented in the table UI (array of credential objects).
 * @param {boolean} [isDuplicate=false] - Flag indicating if the script being saved is a duplicate. If true, checks if all original credentials have been updated.
 * @returns {CredentialsRequestType} An object containing the credentials to be saved (`creds`) and an array of keys to be deleted (`deleted`).
 */
const buildCredentialsRequest = (
  oldCreds: { key: string }[] | null,
  tableCreds: CredentialsType[],
  isDuplicate = false,
): CredentialsRequestType => {
  const credsResponse: CredentialsRequestType = {
    creds: {}, // Object to hold key-value pairs of credentials to save/update
    deleted: [], // Array to hold keys of credentials to delete
  }

  // if script is a duplicate, for each old key check if it exists and if it is updated or not
  // if it exists and not updated, then throw error that not all old keys have been given a value
  if (isDuplicate) {
    oldCreds?.forEach((cred) => {
      const tableCred = tableCreds.find((c) => c.key === cred.key)

      if (tableCred && !tableCred.isUpdated) {
        throw new Error(
          i18n.t("scriptDialog.validation.credentialMissingValue", {
            credKey: cred.key,
            ns: "scripts",
          }),
        )
      }
    })
  }

  // An old credential is considered deleted if it's no longer found in the `tableCreds` array.
  oldCreds?.forEach((cred) => {
    const isDeleted = tableCreds.find((c) => c.key === cred.key) === undefined

    if (isDeleted) return credsResponse.deleted.push(cred.key)
  })

  // Process each credential currently in the table state
  tableCreds.forEach(({ key, value, isUpdated }) => {
    if (isUpdated && key !== "") {
      credsResponse.creds[key] = value
    }
  })

  return credsResponse
}

/**
 * Dialog for creating, reading and updating custom script
 *
 * @param {function} onClose handler for closing dialog
 * @param {boolean} isWorkFlowInReadMode
 * @param {object} scriptId
 * @param {string} nodeId
 * @param {"workflow" | "scripts"} [renderView] Specifies the context in which the dialog is used.
 * @param {boolean} isNewScriptTriggered indicator for this dialog should be new with clean slate or reference to existing script
 * @returns {React.ReactElement}
 */
export function SingleScript(props: Readonly<SingleScriptProps>): React.ReactElement {
  const {
    isWorkFlowInReadMode,
    onClose,
    isNewScriptTriggered = false,
    isDuplicate = false,
    isScriptLoading = false,
    scriptData,
    renderView = "workflow",
    openInEditMode = false,
    metaData,
    isVersionDialog = false,
    setIsVersionDialog,
    shouldShowBuildingState = true,
    tab,
  } = props

  const { id: projectId } = useParams<{ id: string }>()
  const queryClient = useQueryClient()

  const { t, i18n } = useTranslation("scripts")
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve script", permissions.konan, flattenedKonanPermissions)

  // states
  const [currentScriptId, setCurrentScriptId] = useState<string | undefined>(scriptData?.uuid)
  const [selectedState, setSelectedState] = useState<ScriptTab>(tab ?? ScriptTab.ScriptCode)

  const [isConsoleExpanded, setIsConsoleExpanded] = useState<boolean>(false)
  const [isLogsExpanded, setIsLogsExpanded] = useState<boolean>(false)

  const [toggleOpenInEditMode, setToggleOpenInEditMode] = useState<{ isOpen: boolean; snippet?: string }>({
    isOpen: openInEditMode,
    snippet: "",
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [testResponse, setTestResponse] = useState<any>("")

  // state to handle the current mode
  const [mode, setMode] = useState<{ create: boolean; edit: boolean }>({
    create: isNewScriptTriggered,
    edit: openInEditMode,
  })

  const [scriptHash, setScriptHash] = useState<string>("")
  const [requirementsHash, setRequirementsHash] = useState<string>(sha256("").toString())
  const [requirementsFile, setRequirementsFile] = useState<RequirementsDataFile>({
    label: t("scriptDialog.labels.requirementsFiles"),
    value: scriptData?.active_version?.requirements_file?.data_file,
    uuid: undefined,
  })

  // Configure intersection observer for infinite scroll functionality
  // Only triggers next page fetch when last item is in view and more data is available
  const { ref: lastRequirementsFileRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView &&
        hasNextPage &&
        !isRequirementsDataFilesLoading &&
        !isRequirementsDataFilesFetching &&
        !isFetchingNextPage &&
        fetchNextPage()
    },
  })

  const createScriptMutation = useMutation<
    AxiosResponse<ScriptGroupRetrieve>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    CreateScriptRequest
  >(KonanAPI.createScript, {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onSuccess: async (res: any) => {
      setCurrentScriptId(res?.data?.uuid)

      await queryClient.invalidateQueries(["scripts", projectId])
      await queryClient.invalidateQueries(["script", projectId, scriptData?.uuid])
      await queryClient.invalidateQueries("getFileContent")
      NotificationUtils.toast(t("scriptDialog.notifications.createScript.success", { scriptName: res?.data?.name }), {
        severity: "success",
      })
      onClose?.()
      setMode({ create: false, edit: false })
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (response: any) => {
      if (response?.response?.data?.details) {
        NotificationUtils.toast(response?.response?.data?.details?.slice(1, -1), {
          severity: "error",
        })
      } else if (response?.response?.data?.name) {
        NotificationUtils.toast(response?.response?.data?.name[0], {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("scriptDialog.notifications.createScript.genericError"), {
          severity: "error",
        })
      }
    },
  })

  // change script version
  const scriptVersionChangeMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries("selectedScriptVersion")
        await queryClient.invalidateQueries(["scriptVersionsList", projectId])
        await queryClient.invalidateQueries(["script", projectId, scriptData?.uuid])
        await queryClient.invalidateQueries(["scripts", projectId])
      },
      onError: () => {
        NotificationUtils.toast(t("scriptDialog.notifications.changeResourceVersion.error"), {
          severity: "error",
        })
      },
    },
  )

  const updateScriptMutation = useMutation<
    AxiosResponse<ScriptGroupRetrieve>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    UpdateScriptRequest
  >(KonanAPI.updateScript, {
    onSuccess: async (res) => {
      setCurrentScriptId(res?.data?.uuid)

      await queryClient.invalidateQueries(["scripts", projectId])
      await queryClient.invalidateQueries(["script", projectId, scriptData?.uuid])

      NotificationUtils.toast(t("scriptDialog.notifications.updateScript.success", { scriptName: res?.data?.name }), {
        severity: "success",
      })
      onClose?.()
      setTimeout(() => {
        setMode({ edit: false, create: false })
      }, 300)
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (response: any) => {
      if (response?.response?.data?.details) {
        NotificationUtils.toast(response?.response?.data?.details?.slice(1, -1), {
          severity: "error",
        })
      } else if (response?.response?.data?.name) {
        NotificationUtils.toast(response?.response?.data?.name[0], {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("scriptDialog.notifications.updateScript.genericError"), {
          severity: "error",
        })
      }
    },
  })

  const deleteCredsMutation = useMutation<AxiosResponse, AxiosError, DeleteScriptCredsRequest>(
    KonanAPI.deleteScriptCreds,
  )

  const testScriptMutation = useMutation<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosResponse<any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >(KonanAPI.testScript, {
    mutationKey: ["Test-script-mutation", scriptData?.uuid],
    onSuccess: async (res) => {
      NotificationUtils.toast(
        res?.data?.succeeded
          ? t("scriptDialog.notifications.testScript.success")
          : t("scriptDialog.notifications.testScript.scriptError"),
        {
          severity: res?.data?.succeeded ? "success" : "error",
        },
      )

      setTestResponse(res?.data)
      // this is the last valid date user can revert to, since testing saves and updates the script
      if (mode.edit) {
        setLastSaved(formik.values)
        setToggleOpenInEditMode({ isOpen: false })
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (response: any) => {
      if (response?.response?.data?.details) {
        NotificationUtils.toast(response?.response?.data?.details?.slice(1, -1), {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("scriptDialog.notifications.testScript.genericError"), {
          severity: "error",
        })
      }
    },
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cancelBuildingScriptMutation = useMutation<AxiosResponse, AxiosError, any>(KonanAPI.cancelBuildingScript, {
    mutationKey: ["cancelBuildingScript", scriptData?.uuid, scriptData?.active_version?.version],
    onSuccess: async () => {
      NotificationUtils.toast(t("scriptDialog.notifications.cancelBuildingScript.success"), {
        severity: "success",
      })
      await queryClient.invalidateQueries(["scripts", projectId])
      await queryClient.invalidateQueries(["scriptVersionsList", projectId, scriptData?.uuid, isVersionDialog])
      await queryClient.invalidateQueries(["script", projectId, scriptData?.uuid])
    },
    onError: () => {
      NotificationUtils.toast(t("scriptDialog.notifications.cancelBuildingScript.error"), {
        severity: "error",
      })
    },
  })

  const { mutateAsync: uploadScriptAsyncMutation } = useMutation<DataFileGroup, AxiosError, File>(
    (file: File) => {
      return KonanAPI.uploadDataFile({
        project_uuid: projectId as string,
        file: file,
        type: DataFileGroup.type.SCRIPT,
      })
    },
    {
      mutationKey: "uploadScriptFile",
    },
  )

  const { mutateAsync: uploadRequirementsAsyncMutation } = useMutation<DataFileGroup, AxiosError, File>(
    (file: File) => {
      return KonanAPI.uploadDataFile({
        project_uuid: projectId as string,
        file: file,
        type: DataFileGroup.type.REQUIREMENTS,
      })
    },
    {
      mutationKey: "uploadRequirementsFile",
    },
  )

  const formik = useFormik<ScriptDialogFormik>({
    validationSchema: Yup.object({
      scriptName: Yup.string().required(t("ScriptDialog.validation.scriptNameRequired", { ns: "scripts" })),
    }),
    initialValues: {
      snippet: i18n.resolvedLanguage === "en" ? StaticAnalysisScript : StaticAnalysisScriptEs,
      scriptName: scriptData?.name ?? "",
      isDraft: !isWorkFlowInReadMode,
      showDraftBtn: !isWorkFlowInReadMode,
      testFeatures: {},
      creds: formatCreds(isDuplicate, scriptData?.active_version?.creds_keys as { key: string }[] | null | undefined),
      deletedCred: false,
      id: uuidv4(),
      requirements: "",
      requirementsFileName: undefined,
    },
    onSubmit: async ({ snippet, scriptName, isDraft, requirements, requirementsFileName }) => {
      const staticAnalysis = isStaticAnalysisValid(snippet)
      if (!staticAnalysis.valid && staticAnalysis.reason) {
        NotificationUtils.toast(t("scriptDialog.validation.staticAnalysisFailed", { reason: staticAnalysis.reason }), {
          severity: "error",
        })
      } else {
        let scripFiletUUID = undefined
        let requirementsUUID = requirementsFile.uuid

        // check if the file changed before creating new one
        if ((isDuplicate || scriptHash !== sha256(snippet).toString()) && !!snippet) {
          const fileName = `main-${uuidv4()}.py`
          const fileContentType = "text/x-python"
          const finalFile = createFileFromString(snippet, fileName, fileContentType)
          // upload script file first
          const dataFileResponse = await uploadScriptAsyncMutation(finalFile)
          scripFiletUUID = dataFileResponse?.uuid
        }
        if ((isDuplicate || requirementsHash !== sha256(requirements).toString()) && !!requirements) {
          const requirementsContentType = "text/plain"
          // converting string to file object
          const RequirementsFile = createFileFromString(
            requirements,
            requirementsFileName ??
              `requirements_${new Date()
                .toISOString()
                .replace(/[-:T.]/g, "")
                .slice(0, 15)}.txt`,
            requirementsContentType,
          )
          // upload requirements file
          const dataRequirementsResponse = await uploadRequirementsAsyncMutation(RequirementsFile)
          requirementsUUID = dataRequirementsResponse?.uuid
        }
        try {
          // check if script exists or not first
          if ((currentScriptId || scriptData?.uuid) && !isDuplicate) {
            const creds = buildCredentialsRequest(
              scriptData?.active_version?.creds_keys as { key: string }[] | null,
              formik.values.creds,
            )

            if (creds.deleted.length > 0)
              await deleteCredsMutation.mutateAsync({
                project_uuid: projectId as string,
                script_uuid: (scriptData?.uuid ?? currentScriptId) as string,
                creds_keys: creds.deleted,
              })

            // update script first, then testing it
            await updateScriptMutation.mutateAsync({
              file: scripFiletUUID,
              name: scriptName,
              project_uuid: projectId as string,
              is_draft: isDraft,
              script_uuid: (scriptData?.uuid ?? currentScriptId) as string,
              requirements_file: requirementsUUID,
              creds: creds.creds,
            })
          } else {
            const creds = buildCredentialsRequest(
              scriptData?.active_version?.creds_keys as { key: string }[] | null,
              formik.values.creds,
              isDuplicate,
            )

            // create script and then testing it
            await createScriptMutation.mutateAsync({
              file: scripFiletUUID as string,
              name: scriptName,
              project_uuid: projectId as string,
              is_draft: isDraft,
              requirements_file: requirementsUUID,
              creds: creds.creds,
            })
          }
        } catch (e) {
          NotificationUtils.toast((e as Error).message?.toString(), {
            severity: "error",
          })
        }
      }
    },
  })

  const shouldEnableScriptContentFetch = Boolean(
    isDuplicate ||
      (!mode.create &&
        !isNewScriptTriggered &&
        !isScriptLoading &&
        isStaticAnalysisValid(formik.values?.snippet).valid &&
        !testScriptMutation.isLoading),
  )

  const shouldUpdateScript = Boolean(
    !isWorkFlowInReadMode && !isNewScriptTriggered && !isScriptLoading && !testScriptMutation.isLoading,
  )

  const {
    isLoading: isFileLoading,
    isRefetching: isFileRefetching,
    isError: isFetchingFileError,
  } = useQuery(
    ["getFileContent", scriptData?.uuid, scriptData?.active_version?.version],
    async () => (await axios(scriptData?.active_version.file?.data_file as string)).data,
    {
      refetchOnMount: true,
      enabled: shouldEnableScriptContentFetch,
      onSuccess(data: string) {
        if (shouldUpdateScript) {
          formik.setFieldValue("scriptName", scriptData?.name)
          formik.setFieldValue("showDraftBtn", scriptData?.active_version.is_draft)
          formik.setFieldValue("isDraft", scriptData?.active_version.is_draft)
        }

        if (scriptData) {
          formik.setFieldValue("scriptName", `${scriptData?.name}${isDuplicate ? t("scriptDialog.labels.copy") : ""}`)
        }

        if (toggleOpenInEditMode.isOpen) {
          setToggleOpenInEditMode({ isOpen: true, snippet: data })
        }

        formik.setFieldValue("snippet", data)
        setScriptHash(sha256(data).toString())
      },
      onError() {
        NotificationUtils.toast(t("scriptDialog.notifications.fetchFile.error"), {
          severity: "error",
        })
      },
    },
  )

  // fetch requirements file
  const { isLoading: isRequirementsFileLoading } = useQuery(
    ["getFileContent", requirementsFile.value],
    async () => (await axios(requirementsFile.value as string)).data,
    {
      refetchOnMount: true,
      onSuccess(data: string) {
        formik.setFieldValue("requirements", data)
        setRequirementsHash(sha256(data).toString())
      },
      onError() {
        setRequirementsHash(sha256("").toString())
        NotificationUtils.toast(t("scriptDialog.notifications.fetchRequirementsFile.error"), {
          severity: "error",
        })
        console.warn()
      },
      enabled: !!requirementsFile.value,
    },
  )

  // fetch export jobs
  const {
    data: requirementsDataFiles,
    isLoading: isRequirementsDataFilesLoading,
    isFetching: isRequirementsDataFilesFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedDataFileGroupListList>, AxiosError, RequirementsDataFile>(
    ["requirementsDataFiles", projectId],
    ({ pageParam = 1 }) =>
      KonanAPI.fetchDataFiles({
        project_uuid: projectId as string,
        page: pageParam,
        pageSize: 5,
        type: [DataFileGroup.type.REQUIREMENTS],
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce(
          (accumulator, page) =>
            accumulator.concat(
              page.data.results?.map((item: DataFileGroupList, index: number) => ({
                label: item.name,
                value: undefined,
                uuid: item.uuid,
                createdAt: item.created_at,
                index,
              })) as RequirementsDataFile,
            ),
          [] as RequirementsDataFile[],
        ),
      }),
      enabled: !!projectId,
    },
  )

  // saving snapshot of formik before editing
  const [lastSaved, setLastSaved] = React.useState(formik.values)

  // handler for Run test button
  const handleRunTestClicked = async (): Promise<void> => {
    await testScriptMutation.mutateAsync({
      project_uuid: projectId as string,
      script_uuid: metaData?.uuid,
      features: Object.entries(formik.values.testFeatures).reduce(
        (acc, [_, feat]) => {
          return { ...acc, [feat.name]: feat.value }
        },
        {} as Record<string, string>,
      ),
      version: isVersionDialog ? scriptData?.active_version.version : undefined,
    })
    setSelectedState(ScriptTab.ScriptCode)
  }

  const handleCancelBuildingScript = async (): Promise<void> => {
    cancelBuildingScriptMutation.mutateAsync({
      project_uuid: projectId as string,
      script_uuid: scriptData?.uuid,
      version: scriptData?.active_version?.version,
    })
  }

  const isScriptInViewMode = !mode.create && !mode.edit

  const isBuildingState =
    shouldShowBuildingState &&
    (scriptData?.active_version?.status === ScriptVersionRetrieve.status.RUNNING ||
      scriptData?.active_version?.status === ScriptVersionRetrieve.status.PENDING)

  // should disable buttons booleans
  const shouldRunTestButtonDisabled =
    scriptHash !== sha256(formik.values.snippet).toString() ||
    requirementsHash !== sha256(formik.values.requirements).toString() ||
    !!requirementsFile.uuid ||
    isRequirementsFileLoading ||
    isFileLoading ||
    isScriptLoading ||
    testScriptMutation.isLoading ||
    createScriptMutation.isLoading ||
    updateScriptMutation.isLoading

  const shouldEditButtonDisabled =
    formik.isSubmitting ||
    testScriptMutation.isLoading ||
    isScriptLoading ||
    isFileLoading ||
    isRequirementsFileLoading ||
    isBuildingState

  // [TODO]: disable restore when status is pending or cancelled
  const shouldRestoreButtonDisabled =
    testScriptMutation.isLoading || isScriptLoading || scriptVersionChangeMutation.isLoading

  const shouldVersionHistoryButtonDisabled =
    formik.isSubmitting || testScriptMutation.isLoading || isScriptLoading || isFileLoading || isRequirementsFileLoading

  const shouldDisableCancelButton =
    createScriptMutation.isLoading ||
    updateScriptMutation.isLoading ||
    formik.isSubmitting ||
    testScriptMutation.isLoading

  const shouldDisableSaveAsDraftButton =
    createScriptMutation.isLoading ||
    updateScriptMutation.isLoading ||
    formik.isSubmitting ||
    testScriptMutation.isLoading

  const shouldDisableSaveButton =
    !isDuplicate &&
    ((scriptHash === sha256(formik.values.snippet).toString() &&
      requirementsHash === sha256(formik.values.requirements).toString() &&
      scriptData?.name === formik.values.scriptName &&
      !formik.values.creds.some((cred) => cred.isUpdated === true) &&
      !formik.values.deletedCred &&
      !requirementsFile.uuid) ||
      createScriptMutation.isLoading ||
      updateScriptMutation.isLoading ||
      formik.isSubmitting ||
      testScriptMutation.isLoading)

  const handleCancelClicked = async (): Promise<void> => {
    setMode({ edit: false, create: false })

    // to clear requirement file selected
    setRequirementsFile({
      label: t("scriptDialog.labels.requirementsFiles"),
      value: scriptData?.active_version?.requirements_file?.data_file,
      uuid: undefined,
    })

    if (toggleOpenInEditMode.isOpen) {
      formik.setFieldValue("scriptName", scriptData?.name)
      formik.setFieldValue("snippet", toggleOpenInEditMode?.snippet)

      setToggleOpenInEditMode({ isOpen: false })
    } else {
      setTimeout(() => {
        formik.setValues(lastSaved)
        formik.setFieldValue("scriptName", scriptData?.name ?? lastSaved.scriptName)
        formik.setFieldValue(
          "creds",
          formatCreds(isDuplicate, scriptData?.active_version?.creds_keys as unknown as { key: string }[]),
        )
        formik.setFieldValue("deletedCred", false)
      }, 10)
    }
  }

  const extractedConsoleData = useMemo((): string[] => {
    if (testScriptMutation.isError) return [t("extractedConsoleData.failedToExecute")]
    else if (testResponse && !testResponse?.succeeded) return [t("extractedConsoleData.errorCheckLogs")]
    else if (testResponse?.succeeded && testResponse?.output) return [JSON.stringify(testResponse?.output)]
    else return [t("extractedConsoleData.runTestPrompt")]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testResponse, testScriptMutation.isError])

  // used to update formik script name once script request finishes
  useEffect(() => {
    scriptData?.name &&
      scriptData?.name !== formik.values.scriptName &&
      formik.setFieldValue("scriptName", scriptData?.name)

    scriptData?.active_version?.creds_keys &&
      scriptData?.active_version?.creds_keys !== formik.values.creds.map((cred) => cred.key) &&
      formik.setFieldValue(
        "creds",
        formatCreds(isDuplicate, scriptData?.active_version?.creds_keys as unknown as { key: string }[]),
      )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptData?.name, scriptData?.active_version?.creds_keys])

  // Handle if a requirements file already exists for this script.
  useEffect(() => {
    setRequirementsFile({
      label: t("scriptDialog.labels.requirementsFiles"),
      value: scriptData?.active_version?.requirements_file?.data_file,
      uuid: undefined,
    })
  }, [scriptData?.active_version?.requirements_file?.data_file, t])

  const getComponentForTab = (tab: ScriptTab): React.ReactElement => {
    switch (tab) {
      case ScriptTab.Configuration:
        return <CustomScriptsSecrets formik={formik} isReadMode={isScriptInViewMode} isLoading={isScriptLoading} />
      case ScriptTab.ScriptCode:
        return (
          <CustomScriptEditor
            isLoading={isBuildingState ? cancelBuildingScriptMutation.isLoading : isFileLoading || isScriptLoading}
            isRefetchingFile={isFileRefetching}
            script={scriptData}
            isReadMode={isScriptInViewMode}
            formik={formik}
            consoleLogs={extractedConsoleData}
            isFetchingFileError={isFetchingFileError}
            logs={testResponse?.logs}
            isConsoleExpanded={isConsoleExpanded}
            setIsConsoleExpanded={() => setIsConsoleExpanded(!isConsoleExpanded)}
            setIsLogsExpanded={() => setIsLogsExpanded(!isLogsExpanded)}
            isLogsExpanded={isLogsExpanded}
            onActionButtonClick={isBuildingState ? handleCancelBuildingScript : handleRunTestClicked}
            isActionButtonDisabled={
              isBuildingState ? cancelBuildingScriptMutation.isLoading : shouldRunTestButtonDisabled
            }
            isBuildingState={isBuildingState}
            canShowDetails={canShowDetails}
          />
        )
      case ScriptTab.RequirementsPackages:
        return (
          <CustomRequirementsPackages
            isLoading={isRequirementsFileLoading}
            formik={formik}
            isReadMode={isScriptInViewMode}
            options={
              requirementsDataFiles?.pages?.map((file: RequirementsDataFile, index: number) => {
                if (index === requirementsDataFiles.pages.length - 1) return { ...file, ref: lastRequirementsFileRef }
                return file
              }) ?? []
            }
            isLoadingOptions={isRequirementsDataFilesLoading || isRequirementsDataFilesFetching || isFetchingNextPage}
            setRequirementsFile={setRequirementsFile}
            requirementsFile={requirementsFile}
          />
        )
      case ScriptTab.TestFeatures:
        return <CustomScriptTesting isLoading={testScriptMutation.isLoading} handleRunTest={handleRunTestClicked} />
      default:
        return <ScriptLogs uuid={scriptData?.uuid as string} />
    }
  }
  const dialogTabs = canShowDetails
    ? [
        ScriptTab.Configuration,
        ScriptTab.ScriptCode,
        ScriptTab.RequirementsPackages,
        ScriptTab.TestFeatures,
        ...(isNewScriptTriggered ? [] : [ScriptTab.Logs]),
      ]
    : [ScriptTab.ScriptCode, ScriptTab.TestFeatures]

  return (
    <Grid container item xs={12} flexDirection={"column"} flexWrap={"nowrap"} flex={1}>
      <Grid container item p={2} pb={0} flexDirection={"column"} flexWrap={"nowrap"}>
        <Grid container item xs={12} justifyContent="space-between">
          <Grid item className={styles.scriptSideBar}>
            <CodeIcon sx={{ color: "var(--gray-background-1)" }} />
          </Grid>

          <Grid item xs container direction="column" gap={1}>
            {/* Dialog title */}
            <Grid item>
              <Typography variant="label-bold" variantColor={2}>
                {t("scriptDialog.title")}
              </Typography>
            </Grid>

            <Grid container item xs>
              {isScriptInViewMode && !scriptData?.name && isScriptLoading ? (
                <Grid item mt={1}>
                  <Skeleton width={"60%"} height={18} />
                </Grid>
              ) : isScriptInViewMode && (metaData?.name || formik.values.scriptName) ? (
                <Typography variant="h3-bold">{metaData?.name ?? formik.values.scriptName}</Typography>
              ) : (
                <InputText
                  hideDescription
                  key={formik.values.id}
                  disabled={formik.isSubmitting || testScriptMutation.isLoading}
                  type="text"
                  handleChange={formik.handleChange}
                  value={formik.values.scriptName}
                  id="scriptName"
                  widthCalculator
                  placeholder={t("scriptDialog.labels.scriptNamePlaceholder")}
                  error={formik.touched.scriptName && Boolean(formik.errors.scriptName) && formik.errors.scriptName}
                />
              )}
            </Grid>
          </Grid>

          {renderView !== "workflow" && canShowDetails && (
            <Grid container item xs gap={1} mt={1} height="fit-content" display={"flex"} justifyContent="flex-end">
              {isScriptInViewMode ? (
                <Fragment>
                  {!isVersionDialog ? (
                    <Fragment>
                      <Button
                        onClick={setIsVersionDialog}
                        disabled={shouldVersionHistoryButtonDisabled}
                        variant="secondary"
                        size="regular"
                      >
                        {t("scriptDialog.buttons.history")}
                      </Button>

                      <Button
                        onClick={() => {
                          setMode({ create: false, edit: true })
                          setTimeout(() => {
                            setLastSaved(formik.values)
                          }, 10)
                        }}
                        disabled={shouldEditButtonDisabled}
                        variant="secondary"
                        size="regular"
                      >
                        {t("scriptDialog.buttons.edit")}
                      </Button>
                    </Fragment>
                  ) : (
                    !scriptData?.active_version?.is_active_version && (
                      <Button
                        onClick={() => {
                          scriptVersionChangeMutation.mutateAsync({
                            projectId: projectId as string,
                            resource: "scripts",
                            version: scriptData?.active_version.version as string,
                            resourceId: scriptData?.uuid as string,
                          })
                        }}
                        disabled={shouldRestoreButtonDisabled}
                        variant="secondary"
                        size="regular"
                      >
                        {t("scriptDialog.buttons.restore")}
                      </Button>
                    )
                  )}
                </Fragment>
              ) : (
                <Fragment>
                  {mode.edit && !isNewScriptTriggered && (
                    <Button
                      disabled={shouldDisableCancelButton}
                      onClick={handleCancelClicked}
                      variant="secondary"
                      size="regular"
                    >
                      {t("scriptDialog.buttons.cancel")}
                    </Button>
                  )}

                  {formik.values.showDraftBtn && (
                    <Button
                      onClick={() => {
                        formik.setFieldValue("isDraft", true)
                        formik.submitForm()
                      }}
                      disabled={shouldDisableSaveAsDraftButton}
                      variant="secondary"
                      size="regular"
                      isLoading={
                        (createScriptMutation.isLoading || updateScriptMutation.isLoading) && formik.values.isDraft
                      }
                    >
                      {t("scriptDialog.buttons.saveAsDraft")}
                    </Button>
                  )}
                </Fragment>
              )}

              {(mode.create || mode.edit) && (
                <Button
                  disabled={shouldDisableSaveButton}
                  onClick={() => {
                    formik.setFieldValue("isDraft", false)
                    formik.submitForm()
                  }}
                  variant="primary"
                  size="regular"
                  isLoading={createScriptMutation.isLoading || updateScriptMutation.isLoading}
                >
                  {t("scriptDialog.buttons.save")}
                </Button>
              )}
            </Grid>
          )}
        </Grid>

        <Grid container item xs={12} mt={1} className={styles.tabsBottomBorder}>
          <Tabs value={selectedState} className={styles.tabs}>
            {dialogTabs.map((label) => (
              <Tab
                key={label}
                label={t(`types.ScriptTab.${label}`)}
                value={label}
                selected={label === selectedState}
                onClick={() => setSelectedState(label)}
                size="large"
              />
            ))}
          </Tabs>
        </Grid>
      </Grid>
      <Grid container item xs className={styles.scriptDialogTabContent}>
        <FormikProvider value={formik}>{getComponentForTab(selectedState)}</FormikProvider>
      </Grid>
    </Grid>
  )
}

export function ScriptDialog(props: Readonly<ScriptDialogProps>): React.ReactElement {
  const { isOpen, onClose, scriptId, isDuplicate, metaData } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { t } = useTranslation("scripts")

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve script", permissions.konan, flattenedKonanPermissions)

  const MuiTheme = useMuiTheme()

  const [isVersionDialog, setIsVersionDialog] = useState<boolean>(false)
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>()

  // fetch Script
  const { data: scriptData, isLoading: isScriptLoading } = useQuery<AxiosResponse<ScriptGroupRetrieve>, AxiosError>(
    ["script", projectId, scriptId],
    () => KonanAPI.fetchScript({ projectId: projectId as string, scriptId: scriptId as string }),
    {
      onSuccess: ({ data }) => {
        setSelectedVersion(data.active_version.version)
      },
      enabled: canShowDetails && !!projectId && !!scriptId && !isVersionDialog,
    },
  )

  // list Versions
  const {
    isLoading: isVersionsListLoading,
    isFetchingNextPage,
    data: versionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScriptVersionList>, AxiosError>(
    ["scriptVersionsList", projectId, scriptId, isVersionDialog],
    ({ pageParam = 1 }) =>
      KonanAPI.listScriptVersions({
        projectId: projectId as string,
        scriptId: scriptId as string,
        page_size: 10,
        page: pageParam,
      }),
    {
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!scriptId && isVersionDialog,
      refetchOnMount: true,
    },
  )

  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isVersionsListLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  // extracting the results after infinite-Query
  const adjustedScriptVersions = useMemo(() => {
    return (
      versionsList?.pages.reduce(
        (accumulator, page) => accumulator.concat(page.data.results as ScriptVersion[]),
        [] as ScriptVersion[],
      ) ?? []
    )
  }, [versionsList?.pages])

  // fetch Version
  const { data: scriptVersionData, isLoading: isScriptVersionDataLoading } = useQuery<
    AxiosResponse<ScriptVersionRetrieve>,
    AxiosError
  >(
    ["selectedScriptVersion", projectId, scriptId, selectedVersion, isVersionDialog],
    () =>
      KonanAPI.retrieveScriptVersion({
        projectId: projectId as string,
        scriptId: scriptId as string,
        version: selectedVersion as string,
      }),
    {
      enabled: !!projectId && !!scriptId && isVersionDialog && !!selectedVersion,
    },
  )

  return (
    <Dialog
      open={isOpen}
      sx={{
        "& .MuiPaper-root": {
          md: { height: "max(60vh, 700px)" },
        },
      }}
      fullWidth
      maxWidth={isVersionDialog ? "lg" : "md"}
      fullScreen={useMediaQuery(MuiTheme.breakpoints.down("md"))}
      onClose={onClose}
    >
      <DialogTitle
        className="dialog-header-base"
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Box display="flex" gap={1.5} alignItems={"center"}>
          {metaData?.name || (!!scriptData?.data.name && !isDuplicate) ? (
            <Typography variant="h2-bold">{metaData?.name ?? scriptData?.data.name}</Typography>
          ) : isScriptLoading ? (
            <Skeleton variant="rectangular" height={30} width={120} />
          ) : (
            <Typography variant="h2-bold">{t("scriptDialog.title")}</Typography>
          )}
          {scriptData?.data.active_version.version && !isDuplicate && (
            <Tag>{scriptData?.data.active_version.version}</Tag>
          )}
          {scriptData?.data.active_version.status && !isDuplicate && (
            <Tag color={StatusColorMap[scriptData?.data.active_version.status as keyof typeof StatusColorMap]}>
              {t(
                `types.StatusTextMap.${StatusTextMap[scriptData?.data.active_version.status as keyof typeof StatusTextMap]}`,
              )}
            </Tag>
          )}
        </Box>

        <IconButton onClick={onClose} size="small" className={"close-icon-button"}>
          <CloseIcon style={{ color: "var(--base-text-2)" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.scriptDialogContent}>
        {!isVersionDialog ? (
          <SingleScript
            {...props}
            isVersionDialog={isVersionDialog}
            setIsVersionDialog={() => setIsVersionDialog(true)}
            scriptData={scriptData?.data as ScriptGroupRetrieve}
            isScriptLoading={isScriptLoading}
          />
        ) : (
          <Grid container item>
            <Grid item width={"calc(100% - 300px)"} id="single-script" className={styles.mainScriptContainer}>
              <SingleScript
                {...props}
                isVersionDialog={isVersionDialog}
                setIsVersionDialog={() => setIsVersionDialog(true)}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                scriptData={{ ...scriptData?.data, active_version: scriptVersionData?.data as ScriptVersionRetrieve }}
                isScriptLoading={isScriptVersionDataLoading}
              />
            </Grid>

            <Grid item xs={3} minWidth={"300px"}>
              <VersionHistory
                height={520}
                totalCount={versionsList?.pages[0]?.data?.count}
                onClose={() => setIsVersionDialog(false)}
                versions={adjustedScriptVersions ?? []}
                selectedVersion={selectedVersion ?? ""}
                setSelectedVersion={setSelectedVersion}
                isLoading={isVersionsListLoading}
                isScriptView
                activeVersion={scriptData?.data.active_version as ScriptVersionRetrieve}
                infiniteScrollProps={{
                  hasNextPage,
                  isFetchingNextPage,
                  lastVersionRef,
                }}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )
}
